/**
 * @fileoverview Billing admin formatting + status metadata (MOR-438)
 *
 * Pure functions and constants shared across the billing surfaces. Components
 * live in `billingShared.tsx` — mixing component and non-component exports
 * breaks React Fast Refresh.
 */

import type { FunctionReturnType } from "convex/server";
import { api } from "@cvx/_generated/api";
import type { BadgeColor } from "@/ui/primitives/badge";

export type Overview = FunctionReturnType<
  typeof api.admin.billing.getBillingOverview
>;
export type OrgRow = Overview["orgs"][number];
export type Agreement = Overview["agreements"][number];
export type OrgDetail = FunctionReturnType<
  typeof api.admin.billing.getOrgBillingDetail
>;
export type BillingPlan = "pilot" | "subscription" | "per_job";
export type RateSchedule = Agreement["rateSchedule"];

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

/** Whole dollars — for headlines and axis ticks. */
export function formatAud(cents: number): string {
  return (cents / 100).toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
  });
}

/** Exact cents — for ledgers and anything an accountant reconciles. */
export function formatAudExact(cents: number): string {
  return (cents / 100).toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 2,
  });
}

/** "1234.5" (dollars) → 123450 cents, or null when not a positive number. */
export function parseDollarsToCents(input: string): number | null {
  const dollars = Number.parseFloat(input);
  if (!Number.isFinite(dollars) || dollars <= 0) return null;
  return Math.round(dollars * 100);
}

export function centsToDollarsInput(cents: number | null): string {
  return cents === null ? "" : String(cents / 100);
}

export function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** "2026-07" → "Jul 2026" */
export function formatPeriod(yearMonth: string): string {
  const [y, m] = yearMonth.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-AU", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** "2026-07" → "Jul 26" — compact enough for an axis tick. */
export function shortMonth(yearMonth: string): string {
  const [y, m] = yearMonth.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-AU", {
    month: "short",
    year: "2-digit",
    timeZone: "UTC",
  });
}

// ---------------------------------------------------------------------------
// Status metadata
// ---------------------------------------------------------------------------

export const PLAN_META: Record<
  BillingPlan,
  { label: string; color: BadgeColor }
> = {
  pilot: { label: "Pilot", color: "gray" },
  subscription: { label: "Subscription", color: "green" },
  per_job: { label: "Per Job", color: "indigo" },
};

export type ChargeStatusKey =
  "draft" | "invoiced" | "paid" | "void" | "overdue";

export const CHARGE_STATUS_META: Record<
  ChargeStatusKey,
  { label: string; color: BadgeColor; barClass: string }
> = {
  paid: { label: "Paid", color: "emerald", barClass: "bg-emerald-500" },
  invoiced: {
    label: "Awaiting payment",
    color: "amber",
    barClass: "bg-amber-400",
  },
  overdue: { label: "Overdue", color: "red", barClass: "bg-red-500" },
  draft: { label: "Draft", color: "gray", barClass: "bg-gray-400" },
  void: { label: "Void", color: "gray", barClass: "bg-gray-300" },
};

export function chargeStatusMeta(status: string) {
  return (
    CHARGE_STATUS_META[status as ChargeStatusKey] ?? {
      label: status,
      color: "gray" satisfies BadgeColor,
      barClass: "bg-gray-400",
    }
  );
}

export const STRIPE_STATUS_COLORS: Record<string, BadgeColor> = {
  active: "emerald",
  trialing: "sky",
  past_due: "red",
  unpaid: "red",
  incomplete: "amber",
  incomplete_expired: "gray",
  canceled: "gray",
  paused: "gray",
};
